// ============================================================================
// ClientAccountPage — route /me/account (guarded). The client's own settings:
//   • Change password (current + new + confirm) → POST /api/client/me/password
//     (useChangeClientPassword). The server verifies the current password (a 401
//     mirrors a wrong-password login) before writing the new argon2 hash to the
//     caller's OWN row only; the existing session stays valid (no re-login).
//   • Sign out — the same action as the top bar, repeated here for discoverability.
// New password must be >= 8 chars (client rule), confirmed, and different from
// the current one — all validated client-side before the request.
//
// Mobile-first Pulse, reduced-motion-aware, EN + 繁中 via i18n keys only. Own
// data only (resolved server-side from the client cookie).
// ============================================================================
import { useMemo, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Badge, Banner, Button, Card, CardHeader, TextField } from "../../components";
import { ApiError } from "../../api";
import { useClientAuth } from "../auth/ClientAuthContext";
import { useChangeClientPassword } from "../hooks";

const EASE = [0.22, 0.61, 0.36, 1] as const;
const MIN_PASSWORD = 8;

export function ClientAccountPage() {
  const { t } = useTranslation("clientPortal");
  const reduce = useReducedMotion();
  const { client, logout } = useClientAuth();

  const firstName =
    (client?.name ?? "").trim().split(/\s+/)[0] || client?.name || "";

  const fade = (delay = 0) => ({
    initial: reduce ? false : { opacity: 0, y: 16 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.45, delay: reduce ? 0 : delay, ease: EASE },
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div {...fade(0)}>
        <p className="font-display text-xs font-bold uppercase tracking-kicker text-coral">
          {t("account.kicker")}
        </p>
        <h1 className="mt-1 font-display text-2xl font-extrabold tracking-tightest text-body">
          {t("account.title")}
        </h1>
        {firstName && (
          <p className="mt-1 text-body-soft">
            {t("account.signedInAs", { name: firstName })}
          </p>
        )}
      </motion.div>

      <motion.div {...fade(0.06)}>
        <PasswordCard reduce={!!reduce} />
      </motion.div>

      {/* Sign out — repeated here for discoverability. */}
      <motion.div {...fade(0.12)}>
        <Card className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="font-display text-lg font-bold text-body">
              {t("account.signOut.title")}
            </h2>
            <p className="mt-0.5 text-sm text-body-soft">
              {t("account.signOut.blurb")}
            </p>
          </div>
          <Button
            variant="secondary"
            size="sm"
            className="w-full sm:w-auto"
            onClick={() => void logout()}
          >
            {t("account.signOut.action")}
          </Button>
        </Card>
      </motion.div>
    </div>
  );
}

// ----------------------------------------------------------------------------
// Change-password card — current + new + confirm, validated client-side. A
// server-rejected current password (401) maps to an inline "wrong current
// password" error on the current field.
// ----------------------------------------------------------------------------
function PasswordCard({ reduce }: { reduce: boolean }) {
  const { t } = useTranslation("clientPortal");
  const change = useChangeClientPassword();

  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [touched, setTouched] = useState(false);
  const [done, setDone] = useState(false);

  const errors = useMemo(() => {
    const e: { current?: string; next?: string; confirm?: string } = {};
    if (!current) e.current = t("account.password.errors.currentRequired");
    if (!next) e.next = t("account.password.errors.newRequired");
    else if (next.length < MIN_PASSWORD)
      e.next = t("account.password.errors.tooShort");
    else if (next === current) e.next = t("account.password.errors.sameAsOld");
    if (!confirm) e.confirm = t("account.password.errors.confirmRequired");
    else if (next !== confirm) e.confirm = t("account.password.errors.mismatch");
    return e;
  }, [current, next, confirm, t]);

  // A server-rejected current password (401) shown on the current field.
  const serverCurrentError =
    change.isError &&
    change.error instanceof ApiError &&
    change.error.status === 401
      ? t("account.password.errors.wrongCurrent")
      : undefined;

  const valid = Object.keys(errors).length === 0;

  const reset = () => {
    setCurrent("");
    setNext("");
    setConfirm("");
    setTouched(false);
  };

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setTouched(true);
    setDone(false);
    if (!valid || change.isPending) return;
    change.reset();
    change.mutate(
      { current_password: current, new_password: next },
      {
        onSuccess: () => {
          setDone(true);
          reset();
        },
      },
    );
  };

  const onAnyChange = () => {
    if (done) setDone(false);
    if (change.isError) change.reset();
  };

  const showErr = (k: keyof typeof errors) => (touched ? errors[k] : undefined);

  // A generic (non-401) failure surfaces as a banner.
  const genericError =
    change.isError &&
    !(change.error instanceof ApiError && change.error.status === 401)
      ? t("account.password.errors.generic")
      : null;

  return (
    <Card className="space-y-5">
      <CardHeader
        title={t("account.password.title")}
        subtitle={t("account.password.subtitle")}
        action={
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShow((s) => !s)}
            aria-pressed={show}
          >
            {show ? t("account.password.hide") : t("account.password.show")}
          </Button>
        }
      />

      {genericError && <Banner tone="danger" title={genericError} />}

      <form className="space-y-4" onSubmit={onSubmit} noValidate>
        <TextField
          label={t("account.password.current")}
          type={show ? "text" : "password"}
          value={current}
          autoComplete="current-password"
          error={showErr("current") ?? serverCurrentError}
          onChange={(e) => {
            setCurrent(e.target.value);
            onAnyChange();
          }}
        />
        <TextField
          label={t("account.password.new")}
          type={show ? "text" : "password"}
          value={next}
          autoComplete="new-password"
          hint={t("account.password.strength")}
          error={showErr("next")}
          onChange={(e) => {
            setNext(e.target.value);
            onAnyChange();
          }}
        />
        <TextField
          label={t("account.password.confirm")}
          type={show ? "text" : "password"}
          value={confirm}
          autoComplete="new-password"
          error={showErr("confirm")}
          onChange={(e) => {
            setConfirm(e.target.value);
            onAnyChange();
          }}
        />

        <div className="flex items-center justify-between gap-3 pt-1">
          <AnimatePresence initial={false}>
            {done && (
              <motion.span
                key="pw-done"
                initial={reduce ? false : { opacity: 0, x: -6 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.28, ease: EASE }}
              >
                <Badge tone="success" dot>
                  {t("account.password.updated")}
                </Badge>
              </motion.span>
            )}
          </AnimatePresence>

          <Button
            type="submit"
            className="ml-auto w-full sm:w-auto"
            disabled={change.isPending}
            aria-disabled={change.isPending}
          >
            {change.isPending
              ? t("account.password.updating")
              : t("account.password.update")}
          </Button>
        </div>
      </form>
    </Card>
  );
}
